import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  Copy, TrendingUp, DollarSign, ShoppingBag, MousePointer,
  Trophy, Download, ChevronRight, Star, Users
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function CrewDashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [visuals, setVisuals] = useState(null);

  useEffect(() => {
    fetchDashboard();
    fetchLeaderboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('customer_token');
      if (!token) {
        navigate('/login?redirect=/crew/dashboard');
        return;
      }

      const response = await fetch(`${API_URL}/api/crew/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.status === 404) {
        toast.error('Vous n\'êtes pas membre du programme Crew');
        navigate('/crew/join');
        return;
      }

      if (!response.ok) throw new Error('Erreur de chargement');
      
      const data = await response.json();
      setDashboard(data);
      
      // Charger les visuels
      const visualsRes = await fetch(`${API_URL}/api/crew/visuals`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (visualsRes.ok) {
        setVisuals(await visualsRes.json());
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const response = await fetch(`${API_URL}/api/crew/leaderboard`);
      if (response.ok) {
        const data = await response.json();
        setLeaderboard(data.leaderboard);
      }
    } catch (error) {
      console.error('Leaderboard error:', error);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copié !`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#C0000C] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!dashboard) return null;

  const { member, monthly_stats, commission_summary, history, level_progress, leaderboard_position } = dashboard;

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-zinc-900 to-black py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white">Dashboard Crew</h1>
            <p className="text-zinc-400">Bienvenue, {member.first_name} !</p>
          </div>
          <div className="flex items-center gap-2 bg-zinc-800/50 rounded-full px-4 py-2">
            <Star className="w-4 h-4 text-yellow-500" />
            <span className="text-white font-medium">{member.level_label}</span>
          </div>
        </div>

        {/* Code & Link */}
        <div className="grid md:grid-cols-2 gap-4 mb-8">
          <div className="bg-zinc-800/30 border border-zinc-700/50 rounded-xl p-6">
            <p className="text-zinc-400 text-sm mb-2">Ton code promo</p>
            <div className="flex items-center justify-between">
              <span className="text-3xl font-bold text-[#C0000C]" data-testid="crew-promo-code">{member.promo_code}</span>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => copyToClipboard(member.promo_code, 'Code promo')}
                className="text-zinc-400 hover:text-white"
                data-testid="copy-code-btn"
              >
                <Copy className="w-5 h-5" />
              </Button>
            </div>
            <p className="text-zinc-500 text-sm mt-2">-10% pour tes amis</p>
          </div>
          
          <div className="bg-zinc-800/30 border border-zinc-700/50 rounded-xl p-6">
            <p className="text-zinc-400 text-sm mb-2">Ton lien de parrainage</p>
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-black/50 rounded-lg px-3 py-2 text-sm text-white font-mono truncate">
                {member.referral_link}
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => copyToClipboard(member.referral_link, 'Lien')}
                className="text-zinc-400 hover:text-white flex-shrink-0"
                data-testid="copy-link-btn"
              >
                <Copy className="w-5 h-5" />
              </Button>
            </div>
            <p className="text-zinc-500 text-sm mt-2">Cookie tracking 30 jours</p>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-zinc-800/30 border border-zinc-700/50 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-500" />
              </div>
              <span className="text-zinc-400 text-sm">CA ce mois</span>
            </div>
            <div className="text-2xl font-bold text-white" data-testid="monthly-revenue">
              {monthly_stats.revenue.toFixed(0)} ₪
            </div>
          </div>
          
          <div className="bg-zinc-800/30 border border-zinc-700/50 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-[#C0000C]/20 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-[#C0000C]" />
              </div>
              <span className="text-zinc-400 text-sm">Commission ce mois</span>
            </div>
            <div className="text-2xl font-bold text-white" data-testid="monthly-commission">
              {monthly_stats.commission.toFixed(0)} ₪
            </div>
          </div>
          
          <div className="bg-zinc-800/30 border border-zinc-700/50 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <ShoppingBag className="w-5 h-5 text-blue-500" />
              </div>
              <span className="text-zinc-400 text-sm">Ventes totales</span>
            </div>
            <div className="text-2xl font-bold text-white" data-testid="total-sales">
              {member.sales_count}
            </div>
          </div>
          
          <div className="bg-zinc-800/30 border border-zinc-700/50 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <MousePointer className="w-5 h-5 text-purple-500" />
              </div>
              <span className="text-zinc-400 text-sm">Clics</span>
            </div>
            <div className="text-2xl font-bold text-white" data-testid="clicks-count">
              {member.clicks_count}
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Level Progress */}
          <div className="bg-zinc-800/30 border border-zinc-700/50 rounded-xl p-6">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-500" />
              Progression niveau
            </h3>
            
            <div className="flex items-center justify-between mb-3">
              <span className="text-white font-medium">{level_progress.current_level}</span>
              {level_progress.next_level && (
                <span className="text-zinc-400">{level_progress.next_level}</span>
              )}
            </div>
            
            <div className="h-3 bg-zinc-700 rounded-full overflow-hidden mb-3">
              <div 
                className="h-full bg-gradient-to-r from-[#C0000C] to-yellow-500 transition-all duration-500"
                style={{ width: `${level_progress.progress_percent}%` }}
              />
            </div>
            
            <div className="flex items-center justify-between text-sm">
              <span className="text-zinc-400">Commission: {(level_progress.current_rate * 100).toFixed(0)}%</span>
              {level_progress.next_level && (
                <span className="text-zinc-400">
                  Encore {level_progress.sales_to_next} vente(s) → {(level_progress.next_rate * 100).toFixed(0)}%
                </span>
              )}
            </div>
          </div>

          {/* Leaderboard Position */}
          <div className="bg-zinc-800/30 border border-zinc-700/50 rounded-xl p-6">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-500" />
              Leaderboard mensuel
            </h3>
            
            <div className="text-center py-4">
              <div className="text-5xl font-black text-white mb-2" data-testid="leaderboard-position">
                #{leaderboard_position.position}
              </div>
              <p className="text-zinc-400">
                sur {leaderboard_position.total_participants} participant(s)
              </p>
              <p className="text-[#C0000C] font-medium mt-2">
                {leaderboard_position.revenue.toFixed(0)} ₪ générés ce mois
              </p>
            </div>
          </div>
        </div>

        {/* Top 10 Leaderboard */}
        {leaderboard.length > 0 && (
          <div className="bg-zinc-800/30 border border-zinc-700/50 rounded-xl p-6 mb-8">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-[#C0000C]" />
              Top 10 du mois
            </h3>
            
            <div className="space-y-2">
              {leaderboard.map((entry, idx) => (
                <div 
                  key={idx}
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    entry.name.includes(member.first_name) ? 'bg-[#C0000C]/20 border border-[#C0000C]/30' : 'bg-zinc-900/50'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <span className={`w-8 h-8 flex items-center justify-center rounded-full font-bold ${
                      idx === 0 ? 'bg-yellow-500 text-black' :
                      idx === 1 ? 'bg-zinc-300 text-black' :
                      idx === 2 ? 'bg-amber-700 text-white' :
                      'bg-zinc-700 text-white'
                    }`}>
                      {entry.rank}
                    </span>
                    <div>
                      <span className="text-white font-medium">{entry.name}</span>
                      <span className="text-zinc-500 text-sm ml-2">{entry.level}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-white font-bold">{entry.monthly_revenue.toFixed(0)} ₪</div>
                    <div className="text-zinc-500 text-sm">{entry.monthly_sales} ventes</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Commission Summary */}
        <div className="bg-zinc-800/30 border border-zinc-700/50 rounded-xl p-6 mb-8">
          <h3 className="text-lg font-bold text-white mb-4">Résumé des commissions</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-yellow-500">
                {(commission_summary?.pending?.total || 0).toFixed(0)} ₪
              </div>
              <div className="text-zinc-400 text-sm">En attente</div>
            </div>
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-blue-500">
                {(commission_summary?.confirmed?.total || 0).toFixed(0)} ₪
              </div>
              <div className="text-zinc-400 text-sm">Confirmées</div>
            </div>
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-green-500">
                {(commission_summary?.due?.total || 0).toFixed(0)} ₪
              </div>
              <div className="text-zinc-400 text-sm">À payer</div>
            </div>
            <div className="bg-zinc-500/10 border border-zinc-500/30 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-zinc-400">
                {member.paid_commission.toFixed(0)} ₪
              </div>
              <div className="text-zinc-400 text-sm">Total payé</div>
            </div>
          </div>
        </div>

        {/* History */}
        {history.length > 0 && (
          <div className="bg-zinc-800/30 border border-zinc-700/50 rounded-xl p-6 mb-8">
            <h3 className="text-lg font-bold text-white mb-4">Historique mensuel</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-zinc-400 text-sm border-b border-zinc-700">
                    <th className="text-left py-2">Mois</th>
                    <th className="text-right py-2">Ventes</th>
                    <th className="text-right py-2">CA généré</th>
                    <th className="text-right py-2">Commission</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h, idx) => (
                    <tr key={idx} className="border-b border-zinc-800">
                      <td className="py-3 text-white">{h.month}</td>
                      <td className="py-3 text-right text-zinc-400">{h.sales}</td>
                      <td className="py-3 text-right text-white">{h.revenue.toFixed(0)} ₪</td>
                      <td className="py-3 text-right text-green-500">{h.commission.toFixed(0)} ₪</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Visuals Download */}
        {visuals && (
          <div className="bg-zinc-800/30 border border-zinc-700/50 rounded-xl p-6">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Download className="w-5 h-5 text-[#C0000C]" />
              Visuels à télécharger
            </h3>
            <p className="text-zinc-400 text-sm mb-4">
              Templates personnalisés avec ton code {visuals.promo_code}
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {visuals.templates.map((template) => (
                <a
                  key={template.id}
                  href={`/crew/visual/${template.id}?code=${visuals.promo_code}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-zinc-900/50 border border-zinc-700 rounded-lg p-4 hover:border-[#C0000C]/50 transition-colors text-center"
                >
                  <div className="w-12 h-12 bg-[#C0000C]/20 rounded-lg mx-auto mb-2 flex items-center justify-center">
                    <Download className="w-6 h-6 text-[#C0000C]" />
                  </div>
                  <div className="text-white text-sm font-medium">{template.name}</div>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CrewDashboardPage;
